(() => {
  const VAT_MULTIPLIER = 1.2;
  const ready = (callback) => document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', callback)
    : callback();

  const escapeHTML = (value = '') => String(value).replace(/[&<>"']/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[character]));

  const getImageUrl = (product) => {
    if (typeof product?.image === 'string') return product.image;
    return product?.image?.url || product?.featured_image?.url || product?.featured_image?.src || product?.featuredImage?.url || '';
  };

  ready(() => {
    const root = document.querySelector('[data-cart-search]');
    if (!root) return;

    const input = root.querySelector('[data-cart-search-input]');
    const resultsBox = root.querySelector('[data-cart-search-results]');
    const currency = root.dataset.currency || window.Shopify?.currency?.active || 'EUR';
    const formatMoney = (value) => new Intl.NumberFormat(
      document.documentElement.lang || 'fr-FR',
      { style: 'currency', currency },
    ).format(Number(value) || 0);

    const labels = {
      empty: root.dataset.i18nEmpty || 'Aucun résultat',
      products: root.dataset.i18nProducts || 'Produits',
      viewAll: root.dataset.i18nViewAll || 'Voir tous les résultats',
      sku: root.dataset.i18nSku || 'Réf. :',
      loading: root.dataset.labelLoading || 'Vérification…'
    };

    let debounceTimer = null;
    let abortController = null;

    const hide = () => {
      if (!resultsBox) return;
      resultsBox.hidden = true;
      resultsBox.innerHTML = '';
      input?.setAttribute('aria-expanded', 'false');
    };

    const renderProduct = (product) => {
      const image = getImageUrl(product);
      const url = product.url || `/products/${product.handle || ''}`;
      const sku = product.sku || product.variants?.[0]?.sku || '';
      const price = Number(product.price ?? product.price_min ?? 0);
      const meta = product.vendor || product.product_type || '';
      const normalized = (value) => String(value || '').trim().toLocaleLowerCase('fr-FR');
      const productTags = Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',');
      const requiresConfiguration = normalized(product.product_type || product.type) === 'profilé'
        || productTags.some((tag) => normalized(tag) === 'profilé')
        || normalized(product.title).startsWith('profilé');

      return `
        <article class="cart-search-card cd13-quick-product" data-cd13-quick-product data-product-url="${escapeHTML(url)}">
          <a class="cart-search-media${image ? '' : ' cart-search-media--empty'}" href="${escapeHTML(url)}" aria-label="${escapeHTML(product.title || '')}">
            ${image ? `<img src="${escapeHTML(image)}" alt="" loading="lazy">` : '<span aria-hidden="true">□</span>'}
          </a>
          <div class="cart-search-content">
            ${meta ? `<span class="cart-search-meta">${escapeHTML(meta)}</span>` : ''}
            <a class="cart-search-title" href="${escapeHTML(url)}">${escapeHTML(product.title || '')}</a>
            ${sku ? `<span class="cart-search-sku">${escapeHTML(labels.sku)} ${escapeHTML(sku)}</span>` : ''}
          </div>
          <div class="cart-search-prices">
            <strong>${escapeHTML(formatMoney(price))} HT</strong>
            <small>${escapeHTML(formatMoney(Math.round(price * VAT_MULTIPLIER)))} TTC</small>
          </div>
          <div class="cd13-quick-product__actions">
            ${requiresConfiguration
              ? `<a class="cd13-quick-add-button" href="${escapeHTML(url)}">Configurer</a>`
              : `<button type="button" class="cd13-quick-add-button" data-cd13-quick-add disabled aria-busy="true">${escapeHTML(labels.loading)}</button><span class="cd13-quick-status" data-cd13-quick-status aria-live="polite"></span>`}
          </div>
          <div class="cd13-quick-panel" data-cd13-quick-panel hidden></div>
        </article>`;
    };

    const render = (products, query) => {
      if (!resultsBox) return;

      if (!products.length) {
        resultsBox.innerHTML = `<div class="cart-search-empty">${escapeHTML(labels.empty)}</div>`;
      } else {
        resultsBox.innerHTML = `
          <div class="cart-search-kicker">${escapeHTML(labels.products)}</div>
          ${products.slice(0, 8).map(renderProduct).join('')}
          <a class="cart-search-all" href="/search?q=${encodeURIComponent(query)}&type=product">${escapeHTML(labels.viewAll)} →</a>`;
      }

      resultsBox.hidden = false;
      input?.setAttribute('aria-expanded', 'true');
      window.CD13QuickAdd?.hydrate(resultsBox);
      document.dispatchEvent(new CustomEvent('cd13:quick-add-rendered', { detail: { scope: resultsBox } }));
    };

    const search = async (query) => {
      abortController?.abort();
      abortController = new AbortController();

      try {
        const parameters = new URLSearchParams({ q: query });
        parameters.set('resources[type]', 'product');
        parameters.set('resources[limit]', '8');
        parameters.set('resources[options][unavailable_products]', 'last');
        parameters.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag');
        const response = await fetch(`/search/suggest.json?${parameters}`, {
          headers: { Accept: 'application/json' },
          signal: abortController.signal
        });
        if (!response.ok) throw new Error(`Search failed: ${response.status}`);
        const data = await response.json();
        render(data?.resources?.results?.products || [], query);
      } catch (error) {
        if (error.name !== 'AbortError') render([], query);
      }
    };

    input?.setAttribute('aria-controls', resultsBox?.id || 'cartSearchResults');
    input?.setAttribute('aria-expanded', 'false');
    input?.setAttribute('aria-autocomplete', 'list');

    input?.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      const query = input.value.trim();
      if (query.length < 2) {
        hide();
        return;
      }
      debounceTimer = window.setTimeout(() => search(query), 180);
    });

    input?.addEventListener('focus', () => {
      if (input.value.trim().length >= 2 && resultsBox?.innerHTML.trim()) {
        resultsBox.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      }
    });

    input?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') hide();
      if (event.key === 'Enter' && input.value.trim().length > 1) {
        event.preventDefault();
        window.location.href = `/search?q=${encodeURIComponent(input.value.trim())}&type=product`;
      }
    });

    document.addEventListener('click', (event) => {
      if (!root.contains(event.target)) hide();
    });
  });
})();
